import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { APP, ROUTES, firstPermittedApp, isPublicHost } from '../config';
import { useAuth } from '../lib/auth';
import { ErrorBox } from '../components/Loader.jsx';

/**
 * Personal (no-organisation) signup on the apex host.
 *
 * Organisation workspaces still start at ``/super_admin/signup``.
 * Tenant subdomains never offer this form — there is already an org.
 */
export default function SignupPage() {
  const { signup, isAuthenticated, bootstrapping, user } = useAuth();
  const navigate = useNavigate();
  const onApex = isPublicHost();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (bootstrapping) return null;
  if (!onApex) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }
  if (isAuthenticated) {
    const target = firstPermittedApp(user);
    return <Navigate to={target ? target.home : ROUTES.ROOT} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== passwordConfirmation) {
      setError({ message: 'Passwords do not match.' });
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await signup({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        password,
        passwordConfirmation,
      });
      const target = firstPermittedApp(created);
      navigate(target ? target.home : ROUTES.ROOT, { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ty-login">
      <div className="ty-login__card">
        <div
          aria-hidden
          style={{
            width: 64,
            height: 64,
            margin: '0 auto 14px',
            borderRadius: 18,
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            fontSize: 28,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            background: 'linear-gradient(135deg, #7980ff 0%, #656cfd 100%)',
            boxShadow: '0 12px 28px rgba(121, 128, 255, 0.35)',
          }}
        >
          {(APP.name || 'T').slice(0, 1).toUpperCase()}
        </div>
        <h1 className="ty-login__title">Create a personal account</h1>
        <p className="ty-login__sub">
          Work on your own tasks and complaints. Collaboration and assigning
          work to other people stays in organisation workspaces.
        </p>

        <form onSubmit={onSubmit}>
          <ErrorBox error={error} />

          <div className="ty-grid-2">
            <div className="ty-field">
              <label htmlFor="first-name">First name</label>
              <input
                id="first-name"
                className="ty-input"
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            <div className="ty-field">
              <label htmlFor="last-name">Last name</label>
              <input
                id="last-name"
                className="ty-input"
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          <div className="ty-field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              className="ty-input"
              type="email"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="ty-field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              className="ty-input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={4}
              required
            />
          </div>

          <div className="ty-field">
            <label htmlFor="password-confirmation">Confirm password</label>
            <input
              id="password-confirmation"
              className="ty-input"
              type="password"
              autoComplete="new-password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              minLength={4}
              required
            />
          </div>

          <div
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', height: 0, overflow: 'hidden' }}
          >
            <label htmlFor="website_url">Website URL</label>
            <input
              id="website_url"
              name="website_url"
              type="text"
              autoComplete="off"
              tabIndex={-1}
              defaultValue=""
            />
          </div>

          <button className="ty-btn" type="submit" disabled={submitting} style={{ width: '100%' }}>
            {submitting ? 'Creating account…' : 'Create personal account'}
          </button>
        </form>

        <p style={{ marginTop: 20, textAlign: 'center', fontSize: 14 }}>
          Already have an account?{' '}
          <Link to={ROUTES.LOGIN} className="ty-link">
            Sign in
          </Link>
        </p>
        <p style={{ marginTop: 12, textAlign: 'center', fontSize: 14 }}>
          Need a team workspace?{' '}
          <a href="/super_admin/signup" className="ty-link">
            Create an organisation
          </a>
        </p>
      </div>
    </div>
  );
}
